import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import { http } from './client';
import { readEventStream } from './eventStream';

/**
 * Options a caller may set on a request.
 *
 * <h2>Deliberately narrower than axios's</h2>
 *
 * Axios accepts around thirty options, several of which would quietly undo what
 * the interceptors are for — `transformResponse` bypasses the error translation,
 * a per-request `baseURL` escapes the proxy, `validateStatus` makes a 500 look
 * like success. Naming the five that are actually wanted means the rest are not
 * reachable by accident, and adding one is a deliberate edit here rather than a
 * decision made at a call site.
 */
export interface RequestOptions {
  /** Query string values. Nested objects and arrays are serialised by axios. */
  params?: Record<string, unknown>;
  /** Extra headers for this one request. */
  headers?: Record<string, string>;
  /** Overrides the client's 30 second default, for a call known to be slow. */
  timeout?: number;
  /** Cancels the request — pass one from `AbortController` or TanStack Query. */
  signal?: AbortSignal;
  /**
   * What the body is read as. `blob` is for a file the server stores, such as an uploaded picture: an image
   * element cannot send the bearer token, so the bytes are fetched here and shown from an object URL.
   */
  responseType?: 'json' | 'blob';
}

/**
 * The one way this application talks to a server.
 *
 * <h2>Why a wrapper rather than the axios instance</h2>
 *
 * Exporting the instance leaks axios into every call site: its response shape,
 * its config type, its `.data`. Changing transport later would then mean editing
 * every file that ever made a request, and there is no single place to see what
 * the application is allowed to do. One seam instead of hundreds.
 *
 * <p>It also returns what callers want. `post<T>()` gives back `T`, not
 * `AxiosResponse<T>`, which removes a `const { data } =` from every call — the
 * kind of ceremony that is eventually got wrong somewhere nobody looks.
 *
 * <h2>What it does not do</h2>
 *
 * No endpoint list, no domain methods, no `signUp()` or `fetchBrands()`. Those
 * belong to the feature that owns them; a class collecting every call in the
 * product couples every feature to every other and grows without limit. This
 * moves bytes and nothing else.
 *
 * <h2>Where the behaviour actually lives</h2>
 *
 * The interceptors, on the instance below: the bearer token, refreshing an
 * expired session and replaying the request, and turning every failure into an
 * {@link ApiError}. This class is the surface; `client.ts` is the substance.
 */
class ApiClient {
  constructor(private readonly transport: AxiosInstance) {}

  async get<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const { data } = await this.transport.get<T>(url, toAxios(options));
    return data;
  }

  async post<T>(url: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { data } = await this.transport.post<T>(url, body, toAxios(options));
    return data;
  }

  async put<T>(url: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { data } = await this.transport.put<T>(url, body, toAxios(options));
    return data;
  }

  async patch<T>(url: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { data } = await this.transport.patch<T>(url, body, toAxios(options));
    return data;
  }

  /**
   * Deletes, and returns whatever came back.
   *
   * <p>A body is allowed because axios permits one, but most endpoints answer 204
   * with nothing — declare `Promise<void>` at the call site when that is the case
   * rather than inventing a type for an empty response.
   */
  async delete<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const { data } = await this.transport.delete<T>(url, toAxios(options));
    return data;
  }

  /**
   * Posts, then reads the answer as a server-sent event stream: `onEvent` gets each event's data
   * as it arrives, and the promise resolves when the server closes the stream.
   *
   * <p>Through the same instance as every other request, so the bearer token, the refresh on an
   * expired session and the {@link ApiError} translation all still apply to the request that
   * opens the stream. Axios's `fetch` adapter is what lets the browser read the body while it is
   * still arriving; the default XHR adapter only hands it over at the end.
   *
   * <p>The timeout covers the whole stream, not just its opening, so a long-running stream needs
   * one set to match. Aborting the signal stops reading and rejects.
   */
  async stream(
    url: string,
    body: unknown,
    onEvent: (data: string) => void,
    options: RequestOptions = {},
  ): Promise<void> {
    const { config } = streamConfig(options);
    const { data } = await this.transport.post<ReadableStream<Uint8Array>>(url, body, config);
    await readEventStream(data, onEvent);
  }

  /**
   * Opens a stream with a GET, for re-attaching to one that is already running. Otherwise
   * {@link stream}: the same token, refresh and {@link ApiError} translation, and the same timeout
   * covering the whole stream. Nothing running is whatever the server answers, typically a 404.
   */
  async attach(
    url: string,
    onEvent: (data: string) => void,
    options: RequestOptions = {},
  ): Promise<void> {
    const { config } = streamConfig(options);
    const { data } = await this.transport.get<ReadableStream<Uint8Array>>(url, config);
    await readEventStream(data, onEvent);
  }
}

/** The config of a request whose answer is read as it arrives. */
function streamConfig(options: RequestOptions): { config: AxiosRequestConfig } {
  const config = toAxios(options);
  config.adapter = 'fetch';
  config.responseType = 'stream';
  // A refusal before the stream starts is a problem document, so the server is told it may
  // answer with one.
  config.headers = {
    Accept: 'text/event-stream, application/problem+json',
    ...options.headers,
  };
  return { config };
}

/**
 * Narrows the options to axios's config.
 *
 * <p>Written out rather than spread, so adding a field to {@link RequestOptions}
 * is a deliberate two-line change and an unexpected property cannot ride through
 * into axios untouched.
 */
function toAxios(options: RequestOptions): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};
  if (options.params) config.params = options.params;
  if (options.headers) config.headers = options.headers;
  if (options.timeout !== undefined) config.timeout = options.timeout;
  if (options.signal) config.signal = options.signal;
  if (options.responseType) config.responseType = options.responseType;
  return config;
}

export const apiClient = new ApiClient(http);
